import { usePathname } from "next/navigation";
import type { ReactElement, ReactNode } from "react";
import { useMemo } from "react";
import type { ExtendedUser } from "@/auth";
import { Icons } from "@/components/icons";
import { accountPathForPersonalDefault } from "@/lib/account-navigation";
import { useStableSession } from "@/lib/auth/use-stable-session";
import {
  SETTINGS_ENTRY_LABELS,
  settingsPathForEmployerDefault,
} from "@/lib/settings-navigation";

// Use the Route type directly throughout the application
export type SubRoute = {
  readonly title: string;
  readonly link: string;
  readonly icon?: ReactNode;
  readonly checkIsActive?: (currentPath: string) => boolean;
};

export type Route = {
  readonly id: string;
  readonly title: string;
  readonly icon?: ReactElement;
  readonly link: string;
  readonly if: boolean | undefined;
  readonly subs?: SubRoute[];
  readonly selectedExactMatchOnly?: boolean;
  readonly selectedMatchFallback?: boolean;
  readonly checkIsActive?: (currentPath: string) => boolean;
};

export type SubRouteWithActive = SubRoute & { readonly isActive: boolean };

export type RouteWithActive = Route & {
  readonly isActive: boolean;
  readonly subs?: SubRouteWithActive[];
};

// Helper functions to determine active state
const applySubRouteIsActive =
  (path: string): ((r: SubRoute) => SubRouteWithActive) =>
  (r: SubRoute): SubRouteWithActive => {
    let isActive = false;
    if (r.checkIsActive !== undefined) {
      isActive = r.checkIsActive(path);
    } else {
      isActive = r.link === path;
    }
    return {
      ...r,
      isActive,
    };
  };

const applyIsActive = (path: string): ((r: Route) => RouteWithActive) => {
  return (r: Route): RouteWithActive => {
    let isActive = false;

    if (r.checkIsActive !== undefined) {
      isActive = r.checkIsActive(path);
    } else {
      // Fallback
      isActive = Boolean(path?.startsWith(r.link));
    }

    const subs = r.subs ? r.subs.map(applySubRouteIsActive(path)) : undefined;

    return {
      ...r,
      isActive,
      subs,
    };
  };
};

// Core route resolution hook
const useResolveRoutes = (
  routesResolver: () => Route[],
  allowAll?: boolean
): RouteWithActive[] => {
  const path = usePathname() ?? "";

  return useMemo(
    () =>
      routesResolver().flatMap((o) =>
        allowAll || o.if ? [applyIsActive(path)(o)] : []
      ),
    [path, allowAll, routesResolver]
  );
};

// User-type specific hooks
export const useEmployeeRoutes = (allowAll?: boolean): RouteWithActive[] =>
  useResolveRoutes(() => employeeRoutesList(), allowAll);

export const useEmployerRoutes = (allowAll?: boolean): RouteWithActive[] =>
  useResolveRoutes(() => employerRoutesList(), allowAll);

export const useGuestRoutes = (allowAll?: boolean): RouteWithActive[] =>
  useResolveRoutes(() => guestRoutesList(), allowAll);

// Admin route hooks
export const useAdminRoutes = (allowAll?: boolean): RouteWithActive[] =>
  useResolveRoutes(() => adminRoutesList(), allowAll);

// Combined hook that returns routes based on user type
export const useUserRoutes = (allowAll?: boolean): RouteWithActive[] => {
  const { data: session } = useStableSession();
  const userType = (session?.user as ExtendedUser)?.type as
    | "employee"
    | "employer";
  const userRole = (session?.user as ExtendedUser)?.role;

  const path = usePathname() ?? "";

  return useMemo(() => {
    let routes: Route[] = [];

    switch (userType) {
      case "employee":
        routes = employeeRoutesList();
        break;
      case "employer":
        routes = employerRoutesList();
        break;
      default:
        routes = guestRoutesList();
        break;
    }

    if (userRole === "admin") {
      routes = [...routes, ...adminRoutesList()];
    }

    return routes.flatMap((o) =>
      allowAll || o.if ? [applyIsActive(path)(o)] : []
    );
  }, [userType, userRole, path, allowAll]);
};

// Site configuration using Route type directly
export type SiteConfig = {
  name: string;
  description: string;
};

const _siteConfig: SiteConfig = {
  name: "VacWork",
  description: "(in stealth mode)",
};

// Route list definitions
const employeeRoutesList = (): Route[] => [
  {
    id: "job-listings",
    title: "Find Jobs",
    icon: <Icons.map className="size-5" />,
    link: "/job-listings",
    if: true,
  },
  {
    id: "applications",
    title: "Applications",
    icon: <Icons.fileStack className="size-5" />,
    link: "/applications",
    if: true,
  },
  {
    id: "resumes",
    title: "Resumes",
    icon: <Icons.file className="size-5" />,
    link: "/resumes",
    if: process.env.NEXT_PUBLIC_IS_EMPLOYEE_RESUMES_READY === "true",
  },
  {
    id: "conversations",
    title: "Inbox",
    icon: <Icons.mail className="size-5" />,
    link: "/conversations",
    if: true,
  },
  {
    id: "favourites",
    title: "Favourites",
    icon: <Icons.favourites className="size-5" />,
    link: "/favourites",
    if: true,
  },
  {
    id: "account",
    title: SETTINGS_ENTRY_LABELS.profile,
    icon: <Icons.horizontalSliders className="size-5" />,
    link: accountPathForPersonalDefault(),
    if: true,
    checkIsActive: (path: string) => path.startsWith("/account"),
  },
];

const employerRoutesList = (): Route[] => [
  {
    id: "job-listings",
    title: "Find Jobs",
    icon: <Icons.map className="size-5" />,
    link: "/job-listings",
    if: true,
  },
  {
    id: "my-jobs",
    title: "My Jobs",
    icon: <Icons.factory className="size-5" />,
    link: "/jobs",
    if: true,
  },

  {
    id: "conversations",
    title: "Inbox",
    icon: <Icons.mail className="size-5" />,
    link: "/conversations",
    if: true,
  },
  {
    id: "analytics",
    title: "Overview",
    icon: <Icons.analytics className="size-5" />,
    link: "/overview",
    if: true,
  },
  {
    id: "settings",
    title: SETTINGS_ENTRY_LABELS.organization,
    icon: <Icons.horizontalSliders className="size-5" />,
    link: settingsPathForEmployerDefault(),
    if: true,
    checkIsActive: (path: string) => path.startsWith("/settings"),
  },
];

const guestRoutesList = (): Route[] => [
  {
    id: "job-listings",
    title: "Jobs",
    icon: <Icons.map className="size-5" />,
    link: "/job-listings",
    if: true,
  },
];

const adminRoutesList = (): Route[] => [
  {
    id: "admin",
    title: "Admin",
    icon: <Icons.shieldCheck className="size-5" />,
    link: "/admin",
    if: true,
    checkIsActive: (path: string) => path.startsWith("/admin"),
    subs: [
      {
        title: "Dashboard",
        link: "/admin",
        icon: <Icons.chart className="size-5" />,
        checkIsActive: (path: string) => path === "/admin",
      },
      {
        title: "Users",
        link: "/admin/users",
        icon: <Icons.users className="size-5" />,
        checkIsActive: (path: string) => path.startsWith("/admin/users"),
      },
      {
        title: "Orgs",
        link: "/admin/organizations",
        icon: <Icons.building className="size-5" />,
        checkIsActive: (path: string) =>
          path.startsWith("/admin/organizations"),
      },
      {
        title: "Jobs",
        link: "/admin/jobs",
        icon: <Icons.briefcase className="size-5" />,
        checkIsActive: (path: string) => path.startsWith("/admin/jobs"),
      },
      {
        title: "Apps",
        link: "/admin/applications",
        icon: <Icons.fileStack className="size-5" />,
        checkIsActive: (path: string) => path.startsWith("/admin/applications"),
      },
      {
        title: "Vettings",
        link: "/admin/vettings",
        icon: <Icons.shieldCheck className="size-5" />,
        checkIsActive: (path: string) => path.startsWith("/admin/vettings"),
      },
      {
        title: "Invites",
        link: "/admin/invites",
        icon: <Icons.send className="size-5" />,
        checkIsActive: (path: string) => path.startsWith("/admin/invites"),
      },
      {
        title: "Usage",
        link: "/admin/usage",
        icon: <Icons.analytics className="size-4" />,
        checkIsActive: (path: string) => path.startsWith("/admin/usage"),
      },
    ],
  },
];
